import { Component, OnInit, Input} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { GridOptions, ColDef } from "ag-grid-community";

import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { PendingFlow } from '../../../../flow/models/flow.model';
import { WorkOrderType, WorkOrderStatus } from '../../../../common/models/work-order.model';
import { WorkOrderSearchSpec } from '../../../../models/work-order-search-spec.model';
import { PermitService } from '../../../../common/services/permit.service';
import { WorkOrderService } from '../../../../common/services/work-order.service';
import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { Subscription, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { CountyInspector, InspectorListSearchCriteria } from "../../../../common/models/permit.model";

@Component({
    selector: 'inspections-internal',
    templateUrl: './inspections-internal.component.html',
    styleUrls: ['./inspections-internal.component.scss'],
    standalone: false
})
export class InspectionsInternalComponent implements OnInit {
    @Input() widgetTitle: string;


    columnApplicationNumber: ColDef = {
        headerClass: "wrapped",
        headerName: 'Application Number',
        field: 'applicationNumber',
        tooltipField: 'applicationNumber',
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: 'applicationNumber'
        },
        minWidth: 160,
        maxWidth: 160
    }

    columnCompany: ColDef = {
        headerClass: "wrapped",
        headerName: 'Company',
        field: 'companyName',
        tooltipField: 'companyName',
        minWidth: 150,
        maxWidth: 200
    }

    columnFlowName: ColDef = {
        headerClass: "wrapped",
        headerName: 'Type',
        field: 'name',
        tooltipField: 'name',
        minWidth: 125,
        maxWidth: 150
    };

    columnStatus: ColDef = {
        headerClass: "wrapped",
        headerName: 'Status',
        field: 'status',
        tooltipValueGetter: this.statusFormatter(this),
        valueGetter: this.statusFormatter(this),
        minWidth: 100,
        maxWidth: 120
    };

    columnInspectionType: ColDef = {
        headerClass: "wrapped",
        headerName: "Type of Inspection",
        field: "inspectionType",
        tooltipField: "inspectionType",
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("inspectionType", "Unavailable"),
        valueGetter: this.formatter.formatPotentiallyBlankField("inspectionType", "Unavailable"),
        minWidth: 150,
        maxWidth: 200
    }

    columnTract: ColDef = {
        headerClass: "wrapped",
        headerName: "Tract < 5 Acres",
        field: "tract",
        tooltipField: "tract",
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("tract", "Unavailable"),
        valueGetter: this.formatter.formatPotentiallyBlankField("tract", "Unavailable"),
        minWidth: 100,
        maxWidth: 150
    }

    columnAPINumber: ColDef = {
        headerClass: "wrapped",
        headerTooltip: "API #",
        headerName: "API #",
        field: "apiNumber",
        tooltipField: "apiNumber",
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("apiNumber", "Unavailable"),
        valueGetter: this.formatter.formatPotentiallyBlankField("apiNumber", "Unavailable"),
        minWidth: 125,
        maxWidth: 125
    }

    columnCounty: ColDef = {
        headerClass: "wrapped",
        headerTooltip: "County",
        headerName: "County",
        valueGetter: this.findCounty("countyNumber"),
        tooltipValueGetter: this.findCounty("countyNumber"),
        minWidth: 125,
        maxWidth: 125
    }

    columnInspector: ColDef = {
        headerClass: "wrapped",
        headerTooltip: "Inspector",
        headerName: "Inspector",
        valueGetter: this.findInspector("countyNumber"),
        tooltipValueGetter: this.findInspector("countyNumber"),
        minWidth: 125,
        maxWidth: 150
    }

    inspectorList: CountyInspector[] = [];

    subscriptions: Subscription = new Subscription();
    data: PendingFlow[];
    gridColumns = [this.columnApplicationNumber, this.columnCompany, this.columnInspectionType, this.columnAPINumber, this.columnCounty, this.columnInspector, this.columnTract, this.columnStatus];
    gridOptions: GridOptions;

    constructor(
        private router: Router,
        private workOrderService: WorkOrderService,
        private formatter: GridFormatterService,
        private permitService: PermitService
	) {
        this.formatter.applyFilterKeyboardHintTooltips(this.gridColumns);
    }

    ngOnInit() {
        this.refreshData();

        this.gridOptions = <GridOptions>{
            columnDefs: this.gridColumns,
            defaultColDef: { filter: true, sortable: true, resizable: true } as ColDef,
            enableBrowserTooltips: false,
            tooltipTrigger: 'focus',
            isExternalFilterPresent: () => false,
            pagination: true,
            paginationAutoPageSize: true,
            suppressHorizontalScroll: false,
            headerHeight: 50
        };

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    findCounty(field: string) {
        return (obj) => {
            let value = "Unavailable";
            if (obj.data && obj.data[field] && obj.data[field].length > 0) {
                let countyNumber = obj.data[field];
                if (countyNumber != null) {
                    let longCounty = parseInt(countyNumber, 10);
                    if (!isNaN(longCounty)) {
                        let inspector = this.inspectorList.find(i => i.countyNumber == longCounty);
                        if (inspector != null) {
                            value = inspector.countyName;
                        }
                    }
                }
            }
           
            return value;
        }
    }

    findInspector(field: string) {
        return (obj) => {
            let value = "Unavailable";
            if (obj.data && obj.data[field] && obj.data[field].length > 0) {
                let countyNumber = obj.data[field];
                if (countyNumber != null) {
                    let longCounty = parseInt(countyNumber, 10);
                    if (!isNaN(longCounty)) {
                        let inspector = this.inspectorList.find(i => i.countyNumber == longCounty);
                        if (inspector != null) {
                            value = inspector.inspectorName;
                        }
                    }
                }
            }
            
            return value;
        }
    }

    refreshData(): void {
        const workOrderSpec = {
            templateIds: PermitService.InspectionFlowTemplateIds,
            types: [WorkOrderType.Review],
            statuses: [WorkOrderStatus.Pending]
        } as WorkOrderSearchSpec;

        let workOrderObs$: Observable<PendingFlow[]> = this.workOrderService.getPendingFlows(workOrderSpec, true)
            .pipe(
                map((data: PendingFlow[]) => {
                    return data.sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
                }),
                tap((data: PendingFlow[]) => {
                    this.data = data;
                })
        );

        let inspectorListObs$ : Observable<CountyInspector[]> = this.permitService.searchInspectorList({} as InspectorListSearchCriteria)
            .pipe(tap((data: CountyInspector[]) => {
                this.inspectorList = data;
            })
        );

        let subs$ = inspectorListObs$.pipe(
            switchMap(() => {
                return workOrderObs$;
            })
        ).subscribe(
            () => { },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(subs$);
    }

    statusFormatter(component) {
        return (obj) => 'Pending';
    }

}
